class FeraShirtStudio extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-config]').textContent);
    this.text = this.config.text;
    this.form = this.querySelector('form');
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.side = 'front';
    this.color = 'Black';
    this.size = 'S';
    this.art = { front: this.emptyArt(), back: this.emptyArt() };
    this.base = {};
    this.ready = false;
    this.busy = false;
    this.loading = new Set();
    const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.controller.signal });
    this.querySelectorAll('[data-side]').forEach(el => on(el, 'click', () => this.selectSide(el.dataset.side)));
    this.querySelectorAll('[name=studio_color]').forEach(el => on(el, 'change', () => { this.color = el.value; this.querySelector('[data-color-label]').textContent = this.text[this.color.toLowerCase()]; this.updateVariant(); this.draw(); this.revealCanvas(); }));
    this.querySelectorAll('[name=studio_size]').forEach(el => on(el, 'change', () => { this.size = el.value; this.updateVariant(); }));
    this.querySelectorAll('[data-design-id]').forEach(el => on(el, 'click', () => this.loadDesign(el.dataset.designId)));
    this.designDialog = this.querySelector('[data-design-dialog]');
    on(this.querySelector('[data-open-library]'), 'click', () => {
      this.error();
      this.designDialog.showModal();
      document.documentElement.classList.add('fera-design-picker-open');
    });
    on(this.querySelector('[data-close-library]'), 'click', () => this.designDialog.close());
    on(this.designDialog, 'close', () => document.documentElement.classList.remove('fera-design-picker-open'));
    on(this.designDialog, 'click', event => {
      if (event.target !== this.designDialog) return;
      const bounds = this.designDialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.designDialog.close();
    });
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    on(this.querySelector('[data-scale]'), 'input', e => { this.art[this.side].width = Number(e.target.value) / 100; this.draw(); });
    on(this.querySelector('[data-rotate]'), 'input', e => { this.art[this.side].rotation = Number(e.target.value); this.draw(); });
    on(this.querySelector('[data-center]'), 'click', () => { Object.assign(this.art[this.side], { x: .5, y: .46, rotation: 0 }); this.selectSide(this.side); });
    on(this.querySelector('[data-remove]'), 'click', () => this.removeArtwork(this.side));
    on(this.canvas, 'pointerdown', e => {
      if (!this.art[this.side].image || this.busy) return;
      const p = this.point(e);
      this.drag = { x: p.x - this.art[this.side].x, y: p.y - this.art[this.side].y };
      this.canvas.setPointerCapture(e.pointerId);
    });
    on(this.canvas, 'pointermove', e => {
      if (!this.drag) return;
      const p = this.point(e);
      this.art[this.side].x = this.clamp(p.x - this.drag.x, .08, .92);
      this.art[this.side].y = this.clamp(p.y - this.drag.y, .12, .9);
      this.draw();
    });
    on(this.canvas, 'pointerup', () => { this.drag = null; });
    on(this.canvas, 'pointercancel', () => { this.drag = null; });
    on(this.canvas, 'keydown', e => {
      const steps = { ArrowLeft: [-.005, 0], ArrowRight: [.005, 0], ArrowUp: [0, -.005], ArrowDown: [0, .005] };
      if (!steps[e.key] || !this.art[this.side].image) return;
      e.preventDefault();
      const [x, y] = steps[e.key];
      this.art[this.side].x = this.clamp(this.art[this.side].x + x, .08, .92);
      this.art[this.side].y = this.clamp(this.art[this.side].y + y, .12, .9);
      this.draw();
    });
    on(this.form, 'submit', e => this.submit(e));
    this.layoutMedia = matchMedia('(min-width: 750px)');
    this.layoutObserver = new ResizeObserver(() => this.alignPanels());
    this.layoutObserver.observe(this.querySelector('.fera-studio__workspace'));
    on(window, 'resize', () => this.alignPanels());
    on(this.layoutMedia, 'change', () => this.alignPanels());
    this.alignPanels();
    this.loadBases();
  }
  alignPanels() {
    const panel = this.querySelector('.fera-studio__panel');
    const stage = this.querySelector('.fera-studio__stage');
    panel.style.height = '';
    panel.style.minHeight = '';
    panel.style.marginTop = '';
    stage.style.minHeight = '';
    if (!this.layoutMedia.matches) return;
    const offset = stage.getBoundingClientRect().top - this.form.getBoundingClientRect().top;
    const height = Math.max(stage.getBoundingClientRect().height, panel.getBoundingClientRect().height);
    panel.style.minHeight = height + 'px';
    panel.style.marginTop = offset + 'px';
    stage.style.minHeight = height + 'px';
  }
  revealCanvas() {
    if (this.motion?.matches || !this.canvas.animate) return;
    this.canvas.getAnimations().forEach(animation => animation.cancel());
    this.canvas.animate([{ opacity: .25, transform: 'translateY(8px) scale(.985)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 480, easing: 'cubic-bezier(.2,.7,.2,1)' });
  }
  emptyArt() { return { image: null, designId: null, title: '', source: '', x: .5, y: .46, width: .32, rotation: 0 }; }
  clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  point(e) { const r = this.canvas.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }; }
  error(message = '') { for (const el of this.querySelectorAll('[data-error], [data-design-error]')) { el.textContent = message; el.hidden = !message; } }
  async image(src, cors = false) {
    const image = new Image();
    if (cors) image.crossOrigin = 'anonymous';
    image.src = src;
    await image.decode();
    return image;
  }
  async loadBases() {
    try {
      await Promise.all(['Black', 'White'].flatMap(color => ['front', 'back'].map(async side => {
        this.base[color + side] = await this.image(this.config.images[color][side], true);
      })));
      if (!this.isConnected) return;
      this.ready = true;
      this.updateVariant();
      this.selectSide(this.side); this.alignPanels();
    } catch { this.error(this.text.load_error); }
  }
  updateVariant() {
    this.variant = this.config.variants.find(v => v.options[0] === this.color && v.options[1] === this.size);
    this.querySelector('[data-variant]').value = this.variant?.id || '';
    const button = this.querySelector('[data-submit]');
    const hasLibrary = this.config.designs.length > 0;
    button.disabled = !hasLibrary || !this.ready || !this.variant?.available || this.busy || this.loading.size > 0;
    button.textContent = !hasLibrary ? this.text.designs_soon : this.busy ? this.text.preparing : this.loading.size ? this.text.loading : this.variant?.available ? this.text.add_to_cart : this.text.unavailable;
  }
  selectSide(side) {
    this.side = side;
    this.querySelectorAll('[data-side]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.side === side)));
    this.querySelector('[data-side-status]').textContent = this.text[side];
    this.querySelector('[data-picker-side]').textContent = this.text[side];
    this.querySelector('[data-selected-design]').textContent = this.art[side].title || this.text.no_design;
    this.querySelector('[data-edit-label]').textContent = this.text[side];
    this.querySelector('[data-adjust]').hidden = !this.art[side].image;
    this.querySelector('[data-scale]').value = this.art[side].width * 100;
    this.querySelector('[data-rotate]').value = this.art[side].rotation;
    this.querySelectorAll('[data-design-id]').forEach(el => {
      el.hidden = false;
      el.setAttribute('aria-pressed', String(el.dataset.designId === this.art[side].designId));
    });
    this.draw(); this.revealCanvas();
    requestAnimationFrame(() => this.alignPanels());
  }
  async loadDesign(id) {
    const side = this.side;
    const design = this.config.designs.find(item => item.id === id);
    if (!design || this.busy) return;
    this.error();
    const token = Symbol();
    this.designRequest ||= {};
    this.designRequest[side] = token;
    this.loading.add(side); this.updateVariant();
    try {
      const image = await this.image(design.src, true);
      if (!this.isConnected || this.designRequest[side] !== token) return;
      this.art[side] = { ...this.emptyArt(), image, designId: design.id, title: design.title, source: design.src };
      this.selectSide(this.side);
      this.designDialog?.close();
    } catch { if (this.designRequest[side] === token) this.error(this.text.design_error); }
    finally { if (this.designRequest[side] === token) this.loading.delete(side); this.updateVariant(); }
  }
  removeArtwork(side) {
    if (this.designRequest) this.designRequest[side] = null;
    this.loading.delete(side);
    this.art[side] = this.emptyArt();
    this.selectSide(side);
    this.updateVariant();
  }
  render(context, side) {
    const scale = context.canvas.width;
    context.clearRect(0, 0, scale, scale);
    const base = this.base[this.color + side];
    if (base) context.drawImage(base, 0, 0, scale, scale);
    const art = this.art[side];
    if (!art.image) return;
    const width = art.width * scale;
    const height = width * art.image.naturalHeight / art.image.naturalWidth;
    context.save();
    context.translate(art.x * scale, art.y * scale);
    context.rotate(art.rotation * Math.PI / 180);
    context.drawImage(art.image, -width / 2, -height / 2, width, height);
    context.restore();
  }
  draw() { this.render(this.ctx, this.side); }
  async previewFile(side) {
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = 1000;
    this.render(canvas.getContext('2d'), side);
    const blob = await new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error()), 'image/png'));
    return new File([blob], 'FERA-' + this.color + '-' + this.size + '-' + side + '-mockup.png', { type: 'image/png' });
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.ready || this.loading.size) return;
    this.error();
    if (!this.config.designs.length) return;
    if (!this.art.front.image && !this.art.back.image) { this.error(this.text.art_required); return; }
    if (!this.variant?.available) { this.error(this.text.unavailable); return; }
    this.busy = true;
    this.updateVariant();
    this.form.querySelectorAll('button,input,textarea').forEach(el => { if (!el.matches('[data-submit]')) el.disabled = true; });
    try {
      for (const side of ['front', 'back']) {
        const transfer = new DataTransfer(); transfer.items.add(await this.previewFile(side));
        this.querySelector('[data-mockup="' + side + '"]').files = transfer.files;
      }
      const plan = ['Color: ' + this.color, 'Size: ' + this.size, 'Coordinates refer to the full 1000x1000 mockup, not physical inches.'];
      for (const side of ['front', 'back']) {
        const art = this.art[side];
        plan.push(art.image ? side + ': center ' + Math.round(art.x * 1000) + ',' + Math.round(art.y * 1000) + '; width ' + Math.round(art.width * 1000) + '; rotation ' + art.rotation + 'deg; original ' + art.image.naturalWidth + 'x' + art.image.naturalHeight : side + ': blank');
      }
      for (const side of ['front', 'back']) {
        const art = this.art[side];
        this.querySelector('[data-design-name="' + side + '"]').value = art.title || this.text.blank;
        this.querySelector('[data-design-source="' + side + '"]').value = art.source || '';
      }
      this.querySelector('[data-placement]').value = plan.join(' | ');
      this.form.querySelectorAll('button,input,textarea').forEach(el => { el.disabled = false; });
      this.querySelector('[data-submit]').disabled = true;
      HTMLFormElement.prototype.submit.call(this.form);
    } catch {
      this.busy = false;
      this.form.querySelectorAll('button,input,textarea').forEach(el => { el.disabled = false; });
      this.updateVariant(); this.error(this.text.prepare_error);
    }
  }
  disconnectedCallback() {
    this.designDialog?.close();
    document.documentElement.classList.remove('fera-design-picker-open');
    this.layoutObserver?.disconnect();
    this.controller?.abort(); this.controller = null;
  }
}
if (!customElements.get('fera-shirt-studio')) customElements.define('fera-shirt-studio', FeraShirtStudio);
